import { ErrorHandler, Inject, Injectable, Injector, NgZone } from "@angular/core";
import { LogService } from "./app/services/api/log.service";
import { catchError, take } from "rxjs/operators";
import _ from "lodash";

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
	private isLoggingError: boolean = false;
	private recentErrors = new Map<string, number>(); // Cache recent errors with timestamps
	private errorLogCooldown = 10000; // Time in milliseconds to keep errors before re-logging

	constructor(
		@Inject(Injector) private injector: Injector,
		private ngZone: NgZone
	) {}

	private get logService(): LogService {
		return this.injector.get(LogService);
	}

	handleError(error: any) {
		if (this.isLoggingError) {
			return;
		}

		const errorString = _.isObject(error)
			? JSON.stringify(error, Object.getOwnPropertyNames(error))
			: String(error);

		// Check if this error has already been logged recently
		if (this.isDuplicateError(errorString)) {
			return;
		}

		this.isLoggingError = true;
		const chunkFailedMessage = /Loading chunk [\d]+ failed/;

		if (chunkFailedMessage.test(error.message)) {
			window.location.reload();
		} else {
			this.ngZone.run(() => {
				console.error(error);
				this.logService
					.log("ERROR", errorString)
					.pipe(
						take(1),
						catchError((logError) => {
							return [];
						})
					)
					.subscribe({
						complete: () => {
							this.isLoggingError = false;
							// Store this error in recentErrors with a timestamp
							this.cacheError(errorString);
						}
					});
			});
		}
	}

	private isDuplicateError(errorString: string): boolean {
		const now = Date.now();
		const lastLogged = this.recentErrors.get(errorString);

		// Check if error was logged recently
		if (lastLogged && now - lastLogged < this.errorLogCooldown) {
			return true; // Skip logging
		}

		// Otherwise, allow logging
		return false;
	}

	private cacheError(errorString: string) {
		const now = Date.now();
		this.recentErrors.set(errorString, now);
		// Clear out old errors periodically
		this.cleanOldErrors(now);
	}

	private cleanOldErrors(currentTime: number) {
		this.recentErrors.forEach((timestamp, errorString) => {
			if (currentTime - timestamp > this.errorLogCooldown) {
				this.recentErrors.delete(errorString);
			}
		});
	}
}
